/** Data-only contract, shared by the host, editor and render worker. */
export const KEYFRAME_EASINGS = [
  'linear', 'ease-in', 'ease-out', 'ease-in-out', 'hold',
] as const;
export type KeyframeEasing = (typeof KEYFRAME_EASINGS)[number];
export const isKeyframeEasing = (value: unknown): value is KeyframeEasing =>
  typeof value === 'string' && KEYFRAME_EASINGS.some((entry) => entry === value);

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const pointsIn = (document: unknown, seen = new Set<object>()): unknown[] => {
  if (!record(document) || !record(document.items) || seen.has(document)) return [];
  seen.add(document);
  return Object.values(document.items).flatMap((item) => {
    if (!record(item)) return [];
    const points = ['positionKeyframes', 'zoomKeyframes'].flatMap((field) =>
      Array.isArray(item[field]) ? item[field] : []);
    // A saved speech restore can later reintroduce original editable clips.
    return record(item.sourceSpeechRestore)
      ? points.concat(pointsIn(item.sourceSpeechRestore.original, seen)) : points;
  });
};
export const hasNonLinearKeyframes = (document: unknown): boolean =>
  pointsIn(document).some((point) => record(point) && point.easing !== undefined && point.easing !== 'linear');

/** Missing is legacy linear. Invalid explicit values are rejected at every JSON boundary. */
export const hasValidKeyframeEasings = (document: unknown): boolean =>
  pointsIn(document).every((point) => record(point) &&
    (point.easing === undefined || isKeyframeEasing(point.easing)));
